import React, { useEffect, useState } from 'react';

/**
 * LoadStuckScreen — renders a recovery UI when the board page has been
 * stuck on the "no board yet" state for too long.
 *
 * Walter 2026-05-28: the blank-page bug some users hit is caused by
 * browser extensions (Ghostery / uBlock / Brave Shields / AdGuard)
 * blocking Firestore's WebChannel transport URLs. The ?zx=<random>
 * cache-buster in those URLs trips ad-blocker filter heuristics meant
 * to catch analytics endpoints. When the WebChannel is blocked, the
 * Firestore client never returns data, the React loading state never
 * resolves, and the user sees an empty cream-colored page with no
 * recovery path.
 *
 * This component replaces the prior "No board selected." stub with a
 * tiered experience:
 *   - 0-5s: a quiet "loading…" placeholder (don't startle users on
 *           slow networks; most loads finish here)
 *   - >5s:  the recovery screen — explicitly names the most likely
 *           cause (extension), gives a Retry button (hard reload),
 *           and links to the Help page for context. If
 *           navigator.onLine === false we say "you're offline"
 *           instead, since that's a different fix (wait + reconnect).
 *
 * Doesn't fire if `board` is defined — the parent only renders this
 * component when there's nothing to show.
 */
export default function LoadStuckScreen({
  // Optional message tweak — caller can override the headline.
  // Useful if we surface this from a non-board context later.
  context = 'board',
}) {
  // Time since this mount — resets if the parent unmounts/remounts
  // (which it will whenever boardId changes via the route).
  const [elapsed, setElapsed] = useState(0);
  const [online, setOnline] = useState(
    typeof navigator !== 'undefined' ? navigator.onLine : true
  );

  useEffect(() => {
    const tick = setInterval(() => setElapsed(e => e + 500), 500);
    // Walter 2026-06-24: when the network returns while we're stuck on this
    // screen, recover automatically. The live Firestore listener may deliver
    // the board (and unmount this screen) on its own — so wait a beat first;
    // only if we're STILL stuck after that grace do we reload to re-establish
    // listeners against the server. We're on the stuck screen, not editing, so
    // a reload here is safe and is the offline-mode "it'll come back by itself"
    // fix (no more dead "retry" that reloads into the same stuck state offline).
    let recoverTimer = null;
    const onOnline = () => {
      setOnline(true);
      if (recoverTimer) clearTimeout(recoverTimer);
      recoverTimer = setTimeout(() => {
        try { window.location.reload(); } catch { /* ignore */ }
      }, 2500);
    };
    const onOffline = () => {
      setOnline(false);
      if (recoverTimer) { clearTimeout(recoverTimer); recoverTimer = null; }
    };
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      clearInterval(tick);
      if (recoverTimer) clearTimeout(recoverTimer);
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  const showRecovery = elapsed >= 5000;
  const offline = !online;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        padding: '20px',
        color: 'var(--text-dim)',
        fontFamily: 'var(--mono)',
        textAlign: 'center',
      }}
    >
      {!showRecovery && (
        <div style={{
          fontSize: 'calc(12px * var(--ui-scale, 1))',
          opacity: 0.6,
        }}>
          loading {context}…
        </div>
      )}

      {showRecovery && (
        <div style={{
          maxWidth: 480,
          width: '100%',
          background: 'var(--surface)',
          border: '1px solid var(--border-bright)',
          borderRadius: 'var(--radius)',
          padding: '28px 28px 22px',
          boxShadow: 'var(--shadow-modal)',
        }}>
          <div style={{
            fontSize: 'calc(15px * var(--ui-scale, 1))',
            fontWeight: 600,
            color: 'var(--text)',
            marginBottom: '8px',
          }}>
            {offline ? "you're offline" : "having trouble loading"}
          </div>

          <div style={{
            fontSize: 'calc(12px * var(--ui-scale, 1))',
            color: 'var(--text-dim)',
            lineHeight: 1.55,
            marginBottom: '20px',
            textAlign: 'left',
          }}>
            {offline ? (
              <>
                we can't reach the server right now. your edits will
                sync automatically once you're back online — feel free
                to wait it out.
              </>
            ) : (
              <>
                we can't load your {context}. the most common cause is a
                browser extension blocking our database — Ghostery,
                uBlock Origin, Brave Shields, or similar.
                <div style={{ marginTop: 10 }}>
                  try one of these:
                </div>
                <ul style={{
                  margin: '6px 0 0',
                  paddingLeft: 20,
                  lineHeight: 1.6,
                }}>
                  <li>whitelist <code style={{ color: 'var(--text)' }}>listicity.app</code> in your extension</li>
                  <li>try a different browser or an Incognito window</li>
                  <li>retry — sometimes the connection just stalls</li>
                </ul>
              </>
            )}
          </div>

          {/* Offline: no reload button — reloading offline just lands back in
              this same stuck state (the "useless offline" loop). Show a quiet
              status line instead; the effect above auto-recovers on reconnect.
              Online (extension-block case): keep help + retry as before. */}
          {offline ? (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: 8,
              color: 'var(--text-dim)',
              fontSize: 'calc(11px * var(--ui-scale, 1))',
              fontStyle: 'italic',
            }}>
              <span style={{
                width: 7, height: 7, borderRadius: '50%',
                background: 'var(--text-dimmer)', display: 'inline-block',
              }} />
              waiting to reconnect…
            </div>
          ) : (
            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '8px',
            }}>
              <a
                href="/help"
                style={{
                  padding: '8px 14px',
                  background: 'transparent',
                  color: 'var(--text-dim)',
                  border: '1px solid var(--border-bright)',
                  borderRadius: 'var(--radius)',
                  fontFamily: 'var(--mono)',
                  fontSize: 'calc(11px * var(--ui-scale, 1))',
                  textDecoration: 'none',
                  cursor: 'pointer',
                }}
              >
                help
              </a>
              <button
                type="button"
                onClick={() => window.location.reload()}
                style={{
                  padding: '8px 16px',
                  background: 'var(--accent)',
                  color: 'var(--bg)',
                  border: '1px solid var(--accent)',
                  borderRadius: 'var(--radius)',
                  fontFamily: 'var(--mono)',
                  fontSize: 'calc(11px * var(--ui-scale, 1))',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                retry
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
